import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetMarkets, dataSetRegions } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { IMarket, Market } from '../../../@types/model/masterData/market/market';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import MarketHttpService from '../../../services/http/masterData/marketHttpService';
import RegionHttpService from '../../../services/http/masterData/regionHttpService';
import Screen from '../../../components/Screen';
import MarketForm from './form/MarketForm';
import { MarketFormValues, IMarketFormValues } from '../../../@types/model/masterData/market/marketFormValues';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { useHistory } from 'react-router';
import { ConfirmationDialog } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { setMarketsMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { IApiError } from '../../../@types/other';
import CustomTable from '../../../components/datagrid/CustomTable';

const MarketScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const markets = useAppSelector(x => x.masterData.markets);
    const regions = useAppSelector(x => x.masterData.regions);
    const organizations = useAppSelector(x => x.masterData.organizations);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);
    const auth = useAppSelector(x => x.auth);
    const pathname = useHistory()?.location?.pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedMarket, setSelectedMarket] = useState<IMarket>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingMarket, setDeletingMarket] = useState<IMarket>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const marketsRes = await MarketHttpService.getMarketData();
                    const marketsData = marketsRes.data;
                    if (marketsData) {
                        const regionsRes = await RegionHttpService.getRegionData();
                        const regionsData = regionsRes.data;
                        dispatch(dataSetRegions(regionsData));
                        dispatch(dataSetMarkets(marketsData));
                    } else {
                        dispatch(dataSetMarkets([]));
                    }
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading markets.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = () => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSelectedMarket(undefined);
        setFormGuid(undefined);
    };

    const editMarket = (market : IMarket) => {
        setSelectedMarket(market);
        setFormGuid(market.guid);
        openDialog();
    };

    const onSubmit = async (values : IMarketFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        payload.guid = formGuid ?? uuidv4();

        try {
            const res = await MarketHttpService.addOrUpdateMarket(Market(payload));
            const updatedList = upsertArrayElement(markets, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetMarkets(updatedList));
            await setMarketsMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedMarket ? 'Market updated successfully.' : 'Market added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating market data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = (market : IMarket) => {
        setDeletingMarket(market);
        setIsDeletePopupOpen(true);
    };

    const closeDeleteConfirmationPopup = () => {
        setDeletingMarket(undefined);
        setIsDeletePopupOpen(false);
    };

    const removeMarket = async () => {
        if (!deletingMarket) return;
        const updatedMarket = { ...deletingMarket, isActive: false };
        setIsLoading(true);
        try {
            const res = await MarketHttpService.deleteMarket(updatedMarket.id);
            const updatedList = upsertArrayElement(markets, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetMarkets(updatedList));
            await setMarketsMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Market deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting market.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const getRows = useCallback(() => {
        return markets.filter((x) => {
            const region = regions.find(y => y.id === x.regionId && y.isActive);
            return region && region.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y));
        });
    }, [markets, regions, selectedOrganizationIds]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return MarketFormValues(selectedMarket, regions);
        }
    }, [selectedMarket, regions, isDialogOpen]);

    const getRegionName = useCallback((regionId : number) => {
        const region = regions.find(x => x.id === regionId);
        return region ? `(${region.code}) ${region.name}` : '';
    }, [regions]);

    const getRegionOrganizations = useCallback((regionId ?: number) => {
        const region = regions.find(x => x.id === regionId);
        if (!region) return '';
        return organizations
            .filter(x => region.organizationIds.some(y => y === x.id))
            .map(x => `(${x.code}) ${x.name}`)
            .join(', ');
    }, [regions, organizations]);

    const rows = useMemo(() => {
        return getRows().map(row => ({
            ...row,
            regionOrganizations: getRegionOrganizations(row.regionId),  // Since we can't reuse regionId
        }));
    }, [getRows, getRegionOrganizations]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(r => pathname.includes(r.url) && r.isActive && r.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Market'
                    isEdit={!!selectedMarket}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <MarketForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IMarket>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editMarket}
                        enableEditing={hasEditingRight}
                        enableDeleting={m => m.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Region', field: 'regionId', formatFunction: getRegionName, enableFiltering: true, enableSorting: true },
                            { title: 'Organizations', field: 'regionOrganizations', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={rows}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Market'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this market?'
                onAccept={removeMarket}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default MarketScreen;
